import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Global upload-progress UX:
// Every request whose body is a FormData (i.e. file upload) gets an
// onUploadProgress handler attached automatically. We surface progress as
// a single persistent sonner toast that updates as % climbs, then auto-
// dismisses on completion. Call sites that pass their own onUploadProgress
// keep working — we only attach when the option is missing. Set
// `skipUploadToast: true` in the request config to opt-out (e.g. for a
// page that renders its own progress bar inline).
let _uploadCounter = 0;
api.interceptors.request.use((config) => {
  const isFormData = typeof FormData !== "undefined"
    && config.data instanceof FormData;
  if (!isFormData) return config;
  if (config.skipUploadToast) return config;
  if (config.onUploadProgress) return config;
  const uid = ++_uploadCounter;
  // Derive a friendly label from the first uploaded file (when possible).
  let label = "Uploading…";
  try {
    for (const [, v] of config.data.entries()) {
      if (v instanceof File) { label = `Uploading ${v.name}`; break; }
    }
  } catch { /* iter() unsupported in some polyfills */ }
  const toastId = `upload-${uid}`;
  // Initial toast at 0%.
  toast.loading(`${label} · 0%`, { id: toastId });
  config.onUploadProgress = (evt) => {
    if (!evt.total) return;
    const pct = Math.min(100, Math.round((evt.loaded / evt.total) * 100));
    if (pct >= 100) {
      // Server-side processing usually takes a moment after the last byte —
      // keep the toast as "Finalizing" until the response comes back.
      toast.loading(`${label} · finalizing…`, { id: toastId });
    } else {
      toast.loading(`${label} · ${pct}%`, { id: toastId });
    }
  };
  // Stash the toastId so the response interceptor can dismiss it.
  config._uploadToastId = toastId;
  return config;
});

// Attempt refresh on 401
let refreshing = null;
api.interceptors.response.use(
  (r) => {
    // Dismiss the upload progress toast (set in the request interceptor)
    // once we get a successful response back.
    const tid = r.config?._uploadToastId;
    if (tid) toast.dismiss(tid);
    return r;
  },
  async (err) => {
    const tid = err.config?._uploadToastId;
    if (tid) toast.dismiss(tid);
    const original = err.config;
    if (err.response?.status === 401 && !original._retry && !original.url.includes("/auth/")) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = api.post("/auth/refresh");
        }
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail, err) {
  // When err is provided, surface ingress-level failures (413/504/HTML
  // body) clearly instead of the generic "Something went wrong" — that
  // bit users hard when a deployed K8s ingress capped uploads and the
  // toast told them nothing.
  if (detail != null) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  }
  // No JSON detail — see if the raw error carries a status code we can use.
  const status = err?.response?.status;
  if (status === 413) return "File is too large for the server — please contact support to lift the limit.";
  if (status === 504 || status === 502) return "The server took too long to respond. Try a smaller file or retry in a minute.";
  if (status === 0 || err?.code === "ERR_NETWORK") return "Network error — check your connection and retry.";
  if (status) return `Request failed (HTTP ${status}). Please retry; if it keeps failing, contact support.`;
  return "Something went wrong.";
}

export default api;
